import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { testRules } from "data/redirection";
import type { AppUser, OrganizationSubscription } from "types";
import { useSession } from "./useSession";
import { useGetActiveSubscriptions } from "#src/hooks";
import { PAYWALL_FLOW } from "@/constants/paywallFlow";
import { useTeacherOnboardingWhitelist } from "pages/teacher-onboarding/hooks/useTeacherOnboardingWhitelist";
import { useCreatorProfile } from "pages/creator-profile/hooks/useCreatorProfile";

/**
 * Evaluates redirect rules synchronously using session data.
 * Session is already loaded when this runs (Element gates on useSession().isLoading).
 *
 * Returns the redirect path or null.
 */
export const useRedirectRules = (): string | null => {
  const { user, organizations, currentOrganization } = useSession();
  const { pathname } = useLocation();
  const { subscriptions, isLoading: isLoadingSubscriptions } =
    useGetActiveSubscriptions();
  const paywallFlow = PAYWALL_FLOW;
  const { enabledOrgIds } = useTeacherOnboardingWhitelist();
  const isOrgWhitelisted = enabledOrgIds.includes(
    currentOrganization?.id ?? ""
  );
  // Skip the profile fetch for orgs not in the pilot — no point hitting the
  // backend to answer a question whose result can't change the outcome.
  const {
    profile,
    isLoading: isLoadingProfile,
    isError: isProfileError,
  } = useCreatorProfile({ userId: user?.id, enabled: isOrgWhitelisted });

  return useMemo(() => {
    if (!user) return null;
    if (isLoadingSubscriptions) return null;

    const userWithOrgs = {
      ...user,
      organizations: organizations.map((org) => org.id),
    } as AppUser;

    const orgSubscription: OrganizationSubscription | null =
      subscriptions && "license_type" in subscriptions
        ? (subscriptions as unknown as OrganizationSubscription)
        : null;

    // While checking (loading, or a transient fetch error), assume "has a
    // profile" so we don't misfire the redirect before we actually know.
    const hasCreatorProfile =
      isLoadingProfile || isProfileError ? true : !!profile;

    return (
      testRules(
        {
          user: userWithOrgs,
          organizationSubscription: orgSubscription,
          paywallFlow,
          isOrgWhitelisted,
          hasCreatorProfile,
        },
        [],
        undefined,
        pathname
      ) ?? null
    );
  }, [
    user,
    organizations,
    pathname,
    subscriptions,
    isLoadingSubscriptions,
    paywallFlow,
    isOrgWhitelisted,
    isLoadingProfile,
    isProfileError,
    profile,
  ]);
};
